import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import { queryParamsRanges } from 'common/constants';
import { QueryParams } from 'features/searchMolecules/searchMoleculesSlice';
import { getDefaultGroupLimit } from 'features/settings/settingsSlice';
import { initialQueryparams } from 'features/workingQuery/workingQuerySlice';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hopsLabel: {
      marginTop: theme.spacing(2),
    },
    formControlRoot: {
      display: 'flex',
    },
  }),
);

interface Props {
  queryParams: QueryParams;
  handleQueryParamsChange: (name: string) => (event: any) => void;
  resetQueryParameters: () => void;
}

const QueryParametersInputs = ({
  queryParams,
  handleQueryParamsChange,
  resetQueryParameters,
}: Readonly<Props>) => {
  const classes = useStyles();
  const { t } = useTranslation(['common', 'search']);
  const defaultGroupLimit = useSelector(getDefaultGroupLimit);

  const refs: { [name: string]: React.RefObject<HTMLInputElement> } = {
    hac: useRef(null),
    rac: useRef(null),
    groupLimit: useRef(null),
  };

  const reset = () => {
    resetQueryParameters();
    Object.keys(refs).forEach((name) => {
      if (refs[name].current !== null) {
        if (name === 'groupLimit') {
          (refs[name].current as HTMLInputElement).value = String(defaultGroupLimit);
        } else {
          (refs[name].current as HTMLInputElement).value = String(
            initialQueryparams[name as keyof typeof initialQueryparams],
          );
        }
      }
    });
  };

  return (
    <FormControl
      margin="normal"
      fullWidth
      component="fieldset"
      classes={{ root: classes.formControlRoot }}
    >
      <FormLabel component="legend">
        {t('search:query_parameters')}
        <Tooltip arrow title={t('reset_to')!}>
          <IconButton aria-label={t('reset')} onClick={reset}>
            <RotateLeftIcon />
          </IconButton>
        </Tooltip>
      </FormLabel>
      <TextField
        id="standard-number"
        label={t('search:hac')}
        defaultValue={queryParams.hac}
        onChange={handleQueryParamsChange('hac')}
        type="number"
        margin="normal"
        inputProps={{
          min: queryParamsRanges['hac'][0],
          max: queryParamsRanges['hac'][1],
          onBlur: (event: any) => {
            event.target.value = queryParams.hac;
          },
          ref: refs.hac,
        }}
      />
      <TextField
        id="standard-number"
        label={t('search:rac')}
        defaultValue={queryParams.rac}
        onChange={handleQueryParamsChange('rac')}
        type="number"
        margin="normal"
        inputProps={{
          min: queryParamsRanges['rac'][0],
          max: queryParamsRanges['rac'][1],
          onBlur: (event: any) => {
            event.target.value = queryParams.rac;
          },
          ref: refs.rac,
        }}
      />
      <FormHelperText className={classes.hopsLabel}>{t('search:hops')}</FormHelperText>
      <RadioGroup
        row
        value={String(queryParams.hops)}
        aria-label="hops"
        name="hops"
        onChange={handleQueryParamsChange('hops')}
      >
        <FormControlLabel labelPlacement="start" value="1" control={<Radio />} label="1" />
        <FormControlLabel labelPlacement="start" value="2" control={<Radio />} label="2" />
      </RadioGroup>
      <TextField
        id="standard-number"
        label={t('search:groupLimit')}
        defaultValue={queryParams.groupLimit}
        onChange={handleQueryParamsChange('groupLimit')}
        type="number"
        margin="normal"
        inputProps={{
          min: queryParamsRanges['groupLimit'][0],
          max: queryParamsRanges['groupLimit'][1],
          onBlur: (event: any) => {
            event.target.value = queryParams.groupLimit;
          },
          ref: refs.groupLimit,
        }}
      />
    </FormControl>
  );
};

export default QueryParametersInputs;
